import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, LogOut } from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import { getMyEnrollments } from '../lib/lms';
import { getMyCertificates } from '../lib/certificates';
import { PageHeader, Panel, PanelHead, Stamp, Stat } from '../components/lms/ui';
import { AccountSkeleton } from '../components/lms/Skeletons';

const Account: React.FC = () => {
  const { user, isEmailVerified, updateProfile, updatePassword, resendVerification, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: '', phone: '' });
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  const [passwords, setPasswords] = useState({ next: '', confirm: '' });
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordErr, setPasswordErr] = useState('');

  const [verifyMsg, setVerifyMsg] = useState('');
  const [stats, setStats] = useState({ courses: 0, certificates: 0 });
  const [courses, setCourses] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    if (user) setProfile({ name: user.name, phone: user.phone || '' });
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    void Promise.all([
      getMyEnrollments(user.id).catch(() => []),
      getMyCertificates(user.id).catch(() => []),
    ]).then(([enrollments, certs]) => {
      if (cancelled) return;
      setStats({ courses: enrollments.length, certificates: certs.length });
      setCourses(enrollments.map((e) => ({ id: e.course_id, title: e.course?.title || e.course_id })));
    });
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return <AccountSkeleton />;

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileErr('');
    setProfileMsg('');
    if (!profile.name.trim()) {
      setProfileErr('Your name cannot be empty.');
      return;
    }
    setProfileBusy(true);
    try {
      await updateProfile(profile);
      setProfileMsg('Profile updated.');
    } catch (err) {
      setProfileErr(err instanceof Error ? err.message : 'Could not update your profile.');
    } finally {
      setProfileBusy(false);
    }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordErr('');
    setPasswordMsg('');
    if (passwords.next.length < 8) {
      setPasswordErr('Use at least 8 characters.');
      return;
    }
    if (passwords.next !== passwords.confirm) {
      setPasswordErr('Passwords do not match.');
      return;
    }
    setPasswordBusy(true);
    try {
      await updatePassword(passwords.next);
      setPasswords({ next: '', confirm: '' });
      setPasswordMsg('Password updated. Keep it safe.');
    } catch (err) {
      setPasswordErr(err instanceof Error ? err.message : 'Could not update your password.');
    } finally {
      setPasswordBusy(false);
    }
  };

  const resend = async () => {
    setVerifyMsg('');
    try {
      await resendVerification(user.email);
      setVerifyMsg('Confirmation email sent — check your inbox.');
    } catch (err) {
      setVerifyMsg(err instanceof Error ? err.message : 'Could not resend the email.');
    }
  };

  const initials = user.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'LS';

  return (
    <div>
      <PageHeader
        title="Student account"
        lede="The details attached to your account, your password, and the record of programmes on file."
        actions={<Link to="/dashboard" className="lms-btn-secondary">Return to overview</Link>}
      />

      <div className="mt-7 flex flex-wrap items-start gap-4 border-y border-rule py-4">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-blue-700 to-sky-500 font-mono text-[12px] font-semibold tracking-[0.08em] text-white shadow-sm shadow-blue-700/20">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="truncate text-[16px] font-semibold text-ink">{user.name}</h2>
            <Stamp tone={user.role === 'ADMIN' ? 'issued' : 'neutral'}>{user.role === 'ADMIN' ? 'Administrator' : 'Student'}</Stamp>
            {isEmailVerified ? <Stamp tone="cleared">Email verified</Stamp> : <Stamp tone="held">Email unverified</Stamp>}
          </div>
          <p className="mt-1 text-[12.5px] text-ink-2">{user.email}{user.phone ? ` · ${user.phone}` : ''}</p>
        </div>
        <span className="w-full font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2 sm:ml-auto sm:w-auto">
          File {user.id.slice(0, 8).toUpperCase()}
        </span>
      </div>

      {!isEmailVerified && (
        <div className="mt-5 border border-warning/25 bg-warning-soft px-4 py-3.5">
          <div className="flex flex-wrap items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-warning" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-warning">Your email is not verified yet</p>
              <p className="mt-0.5 text-[12px] leading-5 text-warning/90">
                Verify it so you can recover your account and receive certificate notices.
              </p>
              {verifyMsg && <p className="mt-1.5 text-[12px] font-medium text-warning" role="status">{verifyMsg}</p>}
            </div>
            <button onClick={() => void resend()} className="lms-btn border border-warning/30 bg-white/60 text-warning hover:bg-white !py-1.5 !text-[11.5px]">
              Resend email
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="space-y-6">
          <Panel>
            <PanelHead title="Profile" action={<span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Student record</span>} />
            <form onSubmit={saveProfile} className="px-5 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="account-name" className="lms-label">Full name</label>
                  <input
                    id="account-name"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="lms-field"
                  />
                </div>
                <div>
                  <label htmlFor="account-phone" className="lms-label">Phone</label>
                  <input
                    id="account-phone"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="+234…"
                    className="lms-field"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="account-email" className="lms-label">Email</label>
                  <input id="account-email" value={user.email} readOnly className="lms-field bg-paper-2/50 text-ink-2" />
                  <p className="mt-1.5 text-[11.5px] leading-5 text-ink-2">
                    Email changes are handled by support for certificate integrity. Write to{' '}
                    <a href="mailto:info@lsem.org.ng" className="lms-link">info@lsem.org.ng</a>.
                  </p>
                </div>
              </div>
              {profileErr && <p className="mt-3 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{profileErr}</p>}
              {profileMsg && (
                <p className="mt-3 flex items-center gap-1.5 border border-success/25 bg-success-soft px-3 py-2 text-[12px] text-success" role="status">
                  <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> {profileMsg}
                </p>
              )}
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-rule pt-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">Changes apply to all LSEM records</p>
                <button type="submit" disabled={profileBusy} className="lms-btn-primary !py-2 !text-[12px]">
                  {profileBusy ? 'Saving…' : 'Save profile'}
                </button>
              </div>
            </form>
          </Panel>

          <Panel>
            <PanelHead title="Security" action={<Stamp tone="neutral">Password</Stamp>} />
            <form onSubmit={savePassword} className="px-5 py-5">
              <p className="max-w-[68ch] text-[12.5px] leading-5 text-ink-2">
                Choose a strong password you do not use on another site. This changes the key to your LSEM file only.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="account-new-password" className="lms-label">New password</label>
                  <input
                    id="account-new-password"
                    type="password"
                    autoComplete="new-password"
                    value={passwords.next}
                    onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                    className="lms-field"
                  />
                </div>
                <div>
                  <label htmlFor="account-confirm-password" className="lms-label">Confirm password</label>
                  <input
                    id="account-confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                    className="lms-field"
                  />
                </div>
              </div>
              {passwordErr && <p className="mt-3 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">{passwordErr}</p>}
              {passwordMsg && (
                <p className="mt-3 flex items-center gap-1.5 border border-success/25 bg-success-soft px-3 py-2 text-[12px] text-success" role="status">
                  <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> {passwordMsg}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-4">
                <Link to="/forgot-password" className="text-[12px] font-medium text-ink-2 underline decoration-rule underline-offset-4 hover:text-ink">
                  Send a reset link instead
                </Link>
                <button type="submit" disabled={passwordBusy} className="lms-btn-primary !py-2 !text-[12px]">
                  {passwordBusy ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          </Panel>
        </div>

        <aside className="space-y-6">
          <Panel>
            <PanelHead title="Standing" />
            <div className="divide-y divide-rule">
              <Stat label="Programmes on file" value={stats.courses} note="Active and completed enrolments" className="px-5 py-4" />
              <Stat label="Certificates" value={stats.certificates} note="Issued and verifiable" className="px-5 py-4" />
            </div>
            <div className="border-t border-rule p-3">
              <Link to="/certificates" className="flex items-center justify-between px-2 py-2 text-[12.5px] font-medium text-ink hover:bg-paper">
                Open certificates <ArrowRight className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.5} />
              </Link>
            </div>
          </Panel>

          {courses.length > 0 && (
            <Panel>
              <PanelHead title="Programmes on file" action={<span className="font-mono text-[9px] text-ink-2">{courses.length}</span>} />
              <ul className="divide-y divide-rule">
                {courses.slice(0, 6).map((c, i) => (
                  <li key={c.id}>
                    <Link to={`/learn/${c.id}`} className="grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-2 px-5 py-3 hover:bg-paper">
                      <span className="font-mono text-[9px] text-ink-2">{String(i + 1).padStart(2, '0')}</span>
                      <span className="truncate text-[12.5px] font-medium text-ink">{c.title}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.5} />
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          <Panel>
            <PanelHead title="Session" />
            <div className="px-5 py-4">
              <p className="text-[12px] leading-5 text-ink-2">
                Signing out clears this browser session. Your work and certificate record remain on file.
              </p>
              <button
                onClick={() => void logout().then(() => navigate('/'))}
                className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-danger hover:underline"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Sign out
              </button>
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  );
};

export default Account;
